import type {
  Settlement,
  DocumentaryEvidence,
  DocumentaryEvidenceAttachment,
  DocumentaryEvidenceCannotBeProvided,
  PayableAllocation,
} from '@spendesk/bookkeeping-core-types';
import type { MonetaryValue } from 'ezmoney';
import { useQueryClient, type QueryKey } from 'react-query';

import { useQuery } from '../../../api/hooks/useQuery';
import { type QueryState } from '../../../api/queryState';
import { useCompanyId } from '../../app/hooks/useCompanyId';
import {
  type ExpenseAccountAutomation,
  type VatAccountAutomation,
  type ExpenseAmountAutomation,
  type MarvinConfidence,
  type AnalyticalFieldAssociationAutomation,
  type DocumentaryEvidenceNumberAutomation,
  type DocumentaryEvidenceDateAutomation,
  getPayableQueryKey,
} from '../../payable';
import type { VatAccount } from '../../payable/components';
import type { PerDiem } from '../../per-diem';
import type { ExpenseBeneficiary } from '../../requests/expense/api/useExpenseBenefericiariesQuery';
import { type Payable as PayableFromAllPayables } from '../payables/containers/PayablePanelContainer';
import { reshapeRestApiToLocal } from '../payables/containers/PayablePanelContainer/hooks';
import type {
  AnalyticalFieldAssociation,
  Payable as PayableFromPrepare,
} from '../prepare-payables/models';
import type { MileageAllowanceDetails } from '../types';
import { reshapePayableFromRestApi } from './prepare-payable/useGetPayableQuery/rest-reshaper';

export function useGetPayableRestQuery<
  TRaw extends object,
  TReshaped extends object,
>(
  payableId: string | null,
  keyFunction: (companyId: string) => QueryKey,
  reshapeFunction: (data: TRaw) => TReshaped,
  options?: {
    withAllocationsCounterparties?: boolean;
    withResolvedAttachmentUrls?: boolean;
  },
): QueryState<TReshaped | undefined> {
  const companyId = useCompanyId();

  return useQuery<TReshaped | undefined, TRaw>({
    key: keyFunction(companyId),
    isEnabled: Boolean(payableId),
    request: {
      type: 'rest',
      target: 'companyAPI',
      endpoint: `/bookkeeping/payable/${payableId}`,
      params: options,
    },
    options: {
      staleTime: 1000 * 90, // 1.5 minutes
    },
    reshapeData: (data) => reshapeFunction(data),
  });
}

export const allPayablesRestQueryKey = (
  companyId: string,
  payableId: string | null,
) => ['getPayableForAllPayables', companyId, payableId];

export function useGetPayableForAllPayablesRestQuery(payableId: string | null) {
  return useGetPayableRestQuery<PayableFromRestApi, PayableFromAllPayables>(
    payableId,
    (companyId) => allPayablesRestQueryKey(companyId, payableId),
    reshapeRestApiToLocal,
    {
      withAllocationsCounterparties: true,
      withResolvedAttachmentUrls: true,
    },
  );
}

export function useInvalidateGetPayableForAllPayablesRestQuery() {
  const companyId = useCompanyId();
  const queryClient = useQueryClient();

  return (payableId: string | null) => {
    if (!payableId) {
      return;
    }

    queryClient.invalidateQueries(
      allPayablesRestQueryKey(companyId, payableId),
    );
  };
}

export function useGetPayableForPrepareRestQuery(payableId: string | null) {
  return useGetPayableRestQuery<PayableFromRestApi, PayableFromPrepare>(
    payableId,
    (companyId) => getPayableQueryKey(companyId, payableId, 'rest'),
    reshapePayableFromRestApi,
  );
}

// #endregion PerDiemDetails types
// TODO: @accounting: eventually take this from bookkeeping-core-types from an
// EnrichedPayable type or directly inferred from the api typed client
// #region Payable types
interface AccountPayableFromApi {
  id: string;
  generalAccountCode: string;
  auxiliaryAccountCode: string | undefined;
}
interface SupplierFromApi {
  kind: 'supplier';
  id: string;
  name: string;
  thumbnailUrl: string;
}
export interface MemberFromApi {
  kind: 'member';
  id: string;
  givenName: string;
  familyName: string;
  email?: string;
  avatar?: string;
  deletedAt?: string;
  pending: boolean;
}
type CounterpartyFromApi = MemberFromApi | SupplierFromApi;
interface TeamFromApi {
  id: string;
  name: string;
}
interface CostCenterFromApi {
  id: string;
  companyId: string;
  name: string;
  ownerId: string;
  viewerIds: string[];
  isDeleted?: boolean;
}
interface ExpenseAccountFromApi {
  id: string;
  name: string;
  description: string;
  companyId: string;
  isArchived: boolean;
  code: string;
  isDefault?: boolean;
  isAvailable?: boolean;
}
interface VatAccountFromApi {
  id: string;
  name: string;
  description: string;
  companyId: string;
  isArchived: boolean;
  code: string;
  rate: string;
  type: 'vat';
  isDefault?: boolean;
}
interface ReverseChargeAccountFromApi {
  id: string;
  name: string;
  description: string;
  companyId: string;
  isArchived: boolean;
  codeDebit: string;
  codeCredit: string;
  type: 'reverseCharge';
}
type TaxAccountFromApi = VatAccountFromApi | ReverseChargeAccountFromApi;
interface PayableLineFromApi {
  taxAccountId: string | undefined;
  expenseAccountId: string | undefined;
  netAmount: MonetaryValue;
  grossAmount: MonetaryValue;
  vatAmount: MonetaryValue;
  vatAdjustment: MonetaryValue;
  vatRate: { amount: number; precision: number };
  natureId: string;
  expenseAccount?: ExpenseAccountFromApi;
  taxAccount?: TaxAccountFromApi;
  analyticalFieldAssociations: AnalyticalFieldAssociation[];
}
export interface BooleanCustomFieldFromApi {
  customField: {
    id: string;
    name: string;
    type: 'boolean';
    is_splittable: number;
    is_required: number;
  };
  value: boolean;
}
export interface ListCustomFieldFromApi {
  customField: {
    id: string;
    name: string;
    type: 'list';
    is_splittable: number;
    is_required: number;
  };
  values: {
    id: string;
    value: string;
  }[];
}
export type CustomFieldFromApi =
  | BooleanCustomFieldFromApi
  | ListCustomFieldFromApi;
export type AutomationFromApi = {
  expenseAccount: ExpenseAccountAutomation | null;
  vatAccount?: VatAccountAutomation | null;
  expenseAmount: ExpenseAmountAutomation | null;
  accountPayable: {
    accountPayableMatch: {
      id: string;
      generalAccountCode: string;
      auxiliaryAccountCode: string | null;
      isDefault: boolean;
      isArchived: boolean;
      kind: 'supplierAccount' | 'employeeAccount';
    };
    isAppliedOnPayable: boolean;
  } | null;
  tax: {
    kind: 'default' | 'recommendation' | 'prediction' | null;
    confidence: MarvinConfidence | null;
    isAppliedOnPayable: boolean;
    taxAccount: VatAccount | null;
    taxAmount: MonetaryValue | null;
  } | null;
  analyticalFieldAssociations?: AnalyticalFieldAssociationAutomation[];
  documentaryEvidenceNumber?: DocumentaryEvidenceNumberAutomation;
  documentaryEvidenceDate?: Omit<
    DocumentaryEvidenceDateAutomation,
    'documentaryEvidenceDate'
  > & { documentaryEvidenceDate: string };
};

type AllocationWithResolvedCounterparty = {
  allocationId: string;
  entityId: string;
  entityType: 'settlement' | 'expense' | 'reversal';
  amount: MonetaryValue;
  settlement?: {
    kind: Settlement['type'];
    transaction: Pick<
      Settlement['transaction'],
      'medium' | 'description' | 'clearingDate'
    >;
  };
  reversal?: {
    id: string;
    documentaryEvidence?: {
      creditNoteNumber?: string;
    };
  };
  expense?: {
    id: string;
    documentaryEvidence?: {
      invoiceNumber?: string;
    };
  };
};
type ValidationAction =
  | {
      kind: 'deductFromInvoiceRequests';
      invoiceRequests: {
        invoiceRequestId: string;
        amount: MonetaryValue;
      }[];
    }
  | {
      kind: 'recordForFutureInvoices';
    }
  | {
      kind: 'reimburseOutsideSpendesk';
    };

type CreditNoteDetails = {
  id: string;
  companyId: string;
  supplierId: string;
  state: 'inReview' | 'partiallyAllocated' | 'allocated' | 'reimbursed';
  validationAction: ValidationAction | undefined;
  firstValidatedAt: Date | undefined;
  requestId: string;
  creditNoteNumber: string;
  referenceInvoiceRequestId?: string;
  issueDate: string;
  isArchived?: boolean;
  referenceInvoiceRequestNumber: string | undefined;
};

type SpendingAllocationPeriod = {
  spendingId: string;
  spendingType: string; // TODO: refine types
  period: {
    from: Date;
    to: Date;
  };
  date: Date | undefined;
};

type DocumentaryEvidenceFromApi<DE> = DE extends {
  attachments: DocumentaryEvidenceAttachment[];
}
  ? Omit<DE, 'attachments'> & {
      attachments: (DocumentaryEvidenceAttachment & {
        mimetype: string;
        url: string;
        downloadUrl: string;
        downloadXmlUrl?: string;
        thumbnailUrl: string;
        attachedAt: string | undefined;
        path: string;
      })[];
    }
  : DE;

export interface PayableFromRestApi {
  accountPayable: AccountPayableFromApi | undefined;
  counterparty: CounterpartyFromApi;
  commitmentCounterparty?: SupplierFromApi;
  team?: TeamFromApi;
  costCenter?: CostCenterFromApi;
  member: MemberFromApi;
  itemLines: PayableLineFromApi[];
  customFields: CustomFieldFromApi[];
  mileage?: MileageAllowanceDetails;
  perDiem?: PerDiem;
  id: string;
  kind: 'expense' | 'reversal';
  version: number;
  inboxOffset: number;
  memberId: string;
  teamId?: string;
  companyId: string;
  amount: MonetaryValue;
  functionalAmount: MonetaryValue;
  description: string;
  dueDate?: string;
  creationDate: string;
  functionalExchangeRate: { amount: number; precision: number };
  state:
    | 'created'
    | 'prepared'
    | 'notBookkept'
    | 'unprepared'
    | 'discarded'
    | 'to_accounting:pending'
    | 'to_accounting:failed'
    | 'in_accounting'
    | 'in_accounting:manually';
  type:
    | 'bill'
    | 'claimedBill'
    | 'mileageExpense'
    | 'perDiemExpense'
    | 'reverseBill';
  legacyType:
    | 'expense'
    | 'invoice'
    | 'single_purchase'
    | 'multi_use_card'
    | 'subscription'
    | 'physical'
    | 'mileage_allowance'
    | 'per_diem_allowance';
  subType:
    | 'expense'
    | 'invoice'
    | 'singlePurchase'
    | 'multiUseCard'
    | 'creditNote'
    | 'subscription'
    | 'physical'
    | 'mileageAllowance'
    | 'perDiemAllowance';
  accountingDate: string | null;
  spendingCommitmentId: string;
  expenseIndex: number;
  reversalId: undefined;
  spendingExchangeRate: { amount: number; precision: number } | undefined;
  invoiceNumber?: string;
  automation?: AutomationFromApi;
  documentaryEvidence: DocumentaryEvidenceFromApi<DocumentaryEvidence>;
  amortisation?: {
    date: { from: string; to: string };
    schemeId?: string;
    schemeName?: string;
  };
  expenseBeneficiaries?: ExpenseBeneficiary[];
  firstExportedAt?: string;
  documentaryEvidenceCannotBeProvided?: DocumentaryEvidenceCannotBeProvided;
  allocations: PayableAllocation[];
  allocationsWithResolvedCounterparties: AllocationWithResolvedCounterparty[];
  creditNoteDetails?: CreditNoteDetails;
  spendingAllocationPeriod: SpendingAllocationPeriod | undefined;
}
// #endregion Payable types
